// src/pages/actividades/components/SeccionesHoy.tsx
import { useState, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, CornerDownRight, Plus, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, toast } from '@/components/ui';
import { useBloques, useCreateBloque, useUpdateBloque, useDeleteBloque } from '@/hooks/queries/useActividades';
import { SortableRow, DragOverlayRow } from './ActividadDnDList';
import { SubtareaInlineAdd } from './SubtareaInlineAdd';
import type { ActividadListItem, PrioridadActividad } from '@/types';

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ─── Droppable Section Container ───────────────────────────────────────────

function DroppableSection({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'min-h-[40px] transition-colors rounded-b-lg',
        isOver && 'bg-primary/5 ring-1 ring-primary/30',
      )}
    >
      {children}
    </div>
  );
}

// ─── Props ─────────────────────────────────────────────────────────────────

interface SeccionesHoyProps {
  items: ActividadListItem[];
  onReorder: (items: { id: string; orden: number }[]) => void;
  onReparent: (id: string, padreId: string | null) => void;
  onComplete: (id: string) => void;
  onReopen: (id: string) => void;
  onStartTimer: (id: string) => void;
  onPauseTimer?: () => void;
  onResumeTimer?: () => void;
  isTimerPaused?: boolean;
  onUpdatePriority?: (id: string, prioridad: PrioridadActividad) => void;
  onToggleFoco?: (id: string) => void;
  onAssignBloque: (actividadId: string, bloqueId: string | null) => void;
  timerActividadId?: string;
  isTimerActive?: boolean;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  subtareasMap: Record<string, ActividadListItem[]>;
  onLoadSubtareas: (id: string) => void;
  selectedIds?: string[];
  onToggleSelect?: (id: string) => void;
}

// ─── Main Component ────────────────────────────────────────────────────────

const UNGROUPED_ID = '__ungrouped__';

export function SeccionesHoy({
  items,
  onReorder,
  onReparent: _onReparent,
  onComplete,
  onReopen,
  onStartTimer,
  onPauseTimer,
  onResumeTimer,
  isTimerPaused,
  onUpdatePriority: _onUpdatePriority,
  onToggleFoco,
  onAssignBloque,
  timerActividadId,
  isTimerActive,
  expandedIds,
  onToggleExpand,
  subtareasMap,
  onLoadSubtareas,
  selectedIds,
  onToggleSelect,
}: SeccionesHoyProps) {
  const hoy = getTodayISO();
  const { data: bloques = [] } = useBloques(hoy);
  const createBloque = useCreateBloque();
  const updateBloque = useUpdateBloque();
  const deleteBloque = useDeleteBloque();

  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [showNewSection, setShowNewSection] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);

  const selectedSet = useMemo(() => selectedIds ? new Set(selectedIds) : null, [selectedIds]);

  // ─── DnD sensors ──────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ─── Group items by bloque_id ──────────────────────────────────────────
  const { grouped, allItemsMap } = useMemo(() => {
    const g = new Map<string, ActividadListItem[]>();
    g.set(UNGROUPED_ID, []);
    for (const bloque of bloques) {
      g.set(bloque.id, []);
    }
    const map = new Map<string, ActividadListItem>();
    for (const item of items) {
      map.set(item.id, item);
      const key = item.bloque_id ?? UNGROUPED_ID;
      if (!g.has(key)) {
        g.get(UNGROUPED_ID)!.push(item);
      } else {
        g.get(key)!.push(item);
      }
    }
    return { grouped: g, allItemsMap: map };
  }, [items, bloques]);

  // ─── Sortable IDs (include expanded subtareas for DnD) ──────────────
  const getSortableIds = useCallback((sectionItems: ActividadListItem[]): string[] => {
    const ids: string[] = [];
    for (const item of sectionItems) {
      ids.push(item.id);
      if (expandedIds.has(item.id) && subtareasMap[item.id]) {
        for (const sub of subtareasMap[item.id]) {
          // Only include subtareas that are NOT in a different section
          if (!sub.bloque_id || sub.bloque_id === item.bloque_id) {
            ids.push(sub.id);
          }
        }
      }
    }
    return ids;
  }, [expandedIds, subtareasMap]);

  // ─── Section helpers ──────────────────────────────────────────────────

  const toggleCollapse = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleCreateSection = useCallback(() => {
    if (!newSectionName.trim()) return;
    createBloque.mutate(
      { nombre: newSectionName.trim(), fecha: hoy, orden: bloques.length },
      {
        onSuccess: () => { setNewSectionName(''); setShowNewSection(false); },
        onError: () => toast.error('Error al crear sección'),
      }
    );
  }, [newSectionName, hoy, bloques.length, createBloque]);

  const handleRename = useCallback((id: string) => {
    if (!editingName.trim()) {
      setEditingId(null);
      return;
    }
    updateBloque.mutate(
      { id, data: { nombre: editingName.trim() } },
      {
        onSuccess: () => setEditingId(null),
        onError: () => { setEditingId(null); toast.error('Error al renombrar sección'); },
      }
    );
  }, [editingName, updateBloque]);

  const handleDelete = useCallback((id: string) => {
    deleteBloque.mutate(id, {
      onError: () => toast.error('Error al eliminar sección'),
    });
  }, [deleteBloque]);

  // ─── DnD handlers ────────────────────────────────────────────────────

  const findContainerForItem = useCallback((itemId: string): string | null => {
    for (const [containerId, containerItems] of grouped) {
      if (containerItems.some((i) => i.id === itemId)) return containerId;
      // Check expanded subtareas within this container
      for (const item of containerItems) {
        if (expandedIds.has(item.id) && subtareasMap[item.id]?.some((s) => s.id === itemId)) {
          return containerId;
        }
      }
    }
    return null;
  }, [grouped, expandedIds, subtareasMap]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);

    // Determine source and target containers
    const sourceContainer = findContainerForItem(activeIdStr);

    // The "over" can be a container ID (section) or another item ID
    let targetContainer: string | null = null;

    // Check if over is a container (section droppable)
    const containerIds = new Set([UNGROUPED_ID, ...bloques.map(b => b.id)]);
    if (containerIds.has(overIdStr)) {
      targetContainer = overIdStr;
    } else {
      // Over is an item — find which container it belongs to
      targetContainer = findContainerForItem(overIdStr);
    }

    if (!sourceContainer || !targetContainer) return;

    // If moved to a different container → assign bloque
    if (sourceContainer !== targetContainer) {
      const newBloqueId = targetContainer === UNGROUPED_ID ? null : targetContainer;
      onAssignBloque(activeIdStr, newBloqueId);
      return;
    }

    // Same container → reorder within section (only for top-level items, not subtasks)
    if (activeIdStr === overIdStr) return;
    const sectionItems = grouped.get(sourceContainer) ?? [];
    const activeIdx = sectionItems.findIndex(i => i.id === activeIdStr);
    const overIdx = sectionItems.findIndex(i => i.id === overIdStr);
    // If either item is a subtask (not in sectionItems), skip reorder
    if (activeIdx === -1 || overIdx === -1) return;

    const reordered = [...sectionItems];
    reordered.splice(activeIdx, 1);
    reordered.splice(overIdx, 0, sectionItems[activeIdx]);
    onReorder(reordered.map((item, idx) => ({ id: item.id, orden: idx })));
  }, [findContainerForItem, grouped, bloques, onAssignBloque, onReorder]);

  const activeItem = useMemo(() => {
    if (!activeId) return null;
    const fromMain = allItemsMap.get(activeId);
    if (fromMain) return fromMain;
    // Check expanded subtareas
    for (const subs of Object.values(subtareasMap)) {
      const sub = subs.find((s) => s.id === activeId);
      if (sub) return sub;
    }
    return null;
  }, [activeId, allItemsMap, subtareasMap]);
  const hasBloques = bloques.length > 0;

  // ─── Render section items ─────────────────────────────────────────────

  const renderSectionItems = (sectionItems: ActividadListItem[]) => {
    if (sectionItems.length === 0) {
      return (
        <div className="py-4 text-center text-xs text-muted-foreground">
          Arrastra actividades aquí
        </div>
      );
    }
    return (
      <div className="space-y-1 p-1">
        {sectionItems.map((item, index) => {
          const isExpanded = expandedIds.has(item.id);
          const subtareas = subtareasMap[item.id] ?? [];
          // Indent: find previous root item in this section to become its subtarea
          let prevRootItem: ActividadListItem | null = null;
          for (let j = index - 1; j >= 0; j--) {
            if (!sectionItems[j].padre_id) { prevRootItem = sectionItems[j]; break; }
          }
          const canIndent = !item.padre_id && prevRootItem !== null;
          return (
            <div key={item.id}>
              <SortableRow
                item={item}
                onComplete={onComplete}
                onReopen={onReopen}
                onStartTimer={onStartTimer}
                onPauseTimer={onPauseTimer}
                onResumeTimer={onResumeTimer}
                isTimerPaused={isTimerPaused}
                onToggleFoco={onToggleFoco ? () => onToggleFoco(item.id) : undefined}
                timerActividadId={timerActividadId}
                isTimerActive={isTimerActive}
                isExpanded={isExpanded}
                onToggleExpand={() => {
                  onToggleExpand(item.id);
                  if (!isExpanded && !subtareasMap[item.id]) {
                    onLoadSubtareas(item.id);
                  }
                }}
                hasSubtareas={(item.subtareas_count ?? 0) > 0 || isExpanded}
                onIndent={canIndent ? () => _onReparent(item.id, prevRootItem!.id) : undefined}
                isSelected={selectedSet?.has(item.id)}
                onToggleSelect={onToggleSelect ? () => onToggleSelect(item.id) : undefined}
              />
              {/* Subtareas + inline add */}
              {isExpanded && (
                <>
                  {subtareas.map((sub) => {
                    // Subtasks shown independently in their own section get a dimmed reference
                    const inOtherSection = sub.bloque_id && sub.bloque_id !== item.bloque_id;
                    if (inOtherSection) {
                      const bloqueName = bloques.find(b => b.id === sub.bloque_id)?.nombre;
                      return (
                        <div key={sub.id} className="ml-8 px-3 py-1 text-xs text-muted-foreground flex items-center gap-1.5">
                          <CornerDownRight className="h-3 w-3" />
                          <span className="truncate">{sub.titulo}</span>
                          <span className="text-muted-foreground/60">→ {bloqueName ?? 'otra sección'}</span>
                        </div>
                      );
                    }
                    return (
                      <SortableRow
                        key={sub.id}
                        item={sub}
                        isSubtarea
                        onComplete={onComplete}
                        onReopen={onReopen}
                        onStartTimer={onStartTimer}
                        onPauseTimer={onPauseTimer}
                        onResumeTimer={onResumeTimer}
                        isTimerPaused={isTimerPaused}
                        onToggleFoco={onToggleFoco ? () => onToggleFoco(sub.id) : undefined}
                        timerActividadId={timerActividadId}
                        isTimerActive={isTimerActive}
                        onOutdent={() => _onReparent(sub.id, null)}
                      />
                    );
                  })}
                  <SubtareaInlineAdd
                    actividadPadreId={item.id}
                    onCreated={() => onLoadSubtareas(item.id)}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-3">
        {/* Named bloques */}
        {bloques.map((bloque) => {
          const sectionItems = grouped.get(bloque.id) ?? [];
          const isCollapsed = collapsedIds.has(bloque.id);
          const isEditing = editingId === bloque.id;

          return (
            <div key={bloque.id} className="border border-border/50 rounded-lg overflow-hidden">
              {/* Section header */}
              <div
                className="group flex items-center gap-2 px-3 py-2 bg-muted/30 cursor-pointer select-none"
                onClick={() => !isEditing && toggleCollapse(bloque.id)}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                )}

                {isEditing ? (
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleRename(bloque.id);
                      if (e.key === 'Escape') setEditingId(null);
                    }}
                    onBlur={() => handleRename(bloque.id)}
                    className="flex-1 text-sm font-medium bg-background border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="flex-1 text-sm font-medium">
                    {bloque.nombre}
                    <span className="ml-2 text-xs text-muted-foreground">({sectionItems.length})</span>
                  </span>
                )}

                {/* Section actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingId(bloque.id);
                      setEditingName(bloque.nombre);
                    }}
                    className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted"
                    title="Renombrar"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(bloque.id);
                    }}
                    className="h-6 w-6 rounded flex items-center justify-center text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30"
                    title="Eliminar sección"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Section content — droppable + sortable */}
              {!isCollapsed && (
                <DroppableSection id={bloque.id}>
                  <SortableContext
                    items={getSortableIds(sectionItems)}
                    strategy={verticalListSortingStrategy}
                  >
                    {renderSectionItems(sectionItems)}
                  </SortableContext>
                </DroppableSection>
              )}
            </div>
          );
        })}

        {/* Ungrouped section */}
        {(grouped.get(UNGROUPED_ID)?.length ?? 0) > 0 || !hasBloques ? (
          <div className={cn(hasBloques && 'border border-border/30 rounded-lg overflow-hidden')}>
            {hasBloques && (
              <div
                className="flex items-center gap-2 px-3 py-2 bg-muted/20 cursor-pointer select-none"
                onClick={() => toggleCollapse(UNGROUPED_ID)}
              >
                {collapsedIds.has(UNGROUPED_ID) ? (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                  Sin agrupar
                  <span className="ml-2 text-xs">({grouped.get(UNGROUPED_ID)?.length ?? 0})</span>
                </span>
              </div>
            )}
            {(!hasBloques || !collapsedIds.has(UNGROUPED_ID)) && (
              <DroppableSection id={UNGROUPED_ID}>
                <SortableContext
                  items={getSortableIds(grouped.get(UNGROUPED_ID) ?? [])}
                  strategy={verticalListSortingStrategy}
                >
                  {renderSectionItems(grouped.get(UNGROUPED_ID) ?? [])}
                </SortableContext>
              </DroppableSection>
            )}
          </div>
        ) : null}

        {/* Add new section */}
        {showNewSection ? (
          <div className="flex items-center gap-2 px-3">
            <input
              type="text"
              value={newSectionName}
              onChange={(e) => setNewSectionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateSection();
                if (e.key === 'Escape') { setShowNewSection(false); setNewSectionName(''); }
              }}
              placeholder="Nombre de la sección..."
              className="flex-1 text-sm border rounded px-2 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <Button size="sm" onClick={handleCreateSection} disabled={!newSectionName.trim()}>
              Crear
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowNewSection(false); setNewSectionName(''); }}>
              Cancelar
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowNewSection(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors w-full"
          >
            <Plus className="h-3.5 w-3.5" />
            Nueva sección
          </button>
        )}
      </div>

      {/* Drag overlay (ghost element) */}
      <DragOverlay>
        {activeItem ? <DragOverlayRow item={activeItem} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
